import type { CSSProperties, ReactNode } from 'react'
import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface AnalysisMarkdownProps {
  text: string
}

const PARAGRAPH_STYLE: CSSProperties = {
  margin: '4px 0 0 0',
}

const UL_STYLE: CSSProperties = {
  margin: '4px 0 0 0',
  paddingLeft: 18,
  listStyleType: 'disc',
  color: 'var(--steel-blue)',
}

const OL_STYLE: CSSProperties = {
  margin: '4px 0 0 0',
  paddingLeft: 22,
  listStyleType: 'decimal',
  color: 'var(--steel-blue)',
}

const LI_STYLE: CSSProperties = {
  color: 'var(--text)',
  marginBottom: 2,
}

const LINK_STYLE: CSSProperties = {
  color: 'var(--steel-blue)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
}

const INLINE_CODE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.92em',
  padding: '1px 5px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 4,
}

const PRE_STYLE: CSSProperties = {
  margin: '6px 0 0 0',
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  overflowX: 'auto',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  lineHeight: 1.45,
  color: 'var(--text)',
}

const BLOCKQUOTE_STYLE: CSSProperties = {
  margin: '6px 0 0 0',
  padding: '4px 0 4px 10px',
  borderLeft: '3px solid var(--steel-blue)',
  color: 'var(--text-2)',
  fontStyle: 'italic',
}

const HEADING_BASE: CSSProperties = {
  margin: '8px 0 2px 0',
  color: 'var(--text-2)',
  fontWeight: 700,
  letterSpacing: '0.02em',
}

const TABLE_WRAP_STYLE: CSSProperties = {
  margin: '6px 0 0 0',
  overflowX: 'auto',
}

const TABLE_STYLE: CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: 12,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  overflow: 'hidden',
}

const TH_STYLE: CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--text-2)',
  fontWeight: 600,
}

const TD_STYLE: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text)',
}

const HR_STYLE: CSSProperties = {
  margin: '10px 0',
  border: 0,
  borderTop: '1px solid var(--border)',
}

/**
 * Markdown renderer for the leave advisor's "Full AI analysis" output.
 *
 * Wraps `react-markdown` (+ GFM: tables, task lists, autolinks, strikethrough)
 * with element-level styling that matches the LMS design tokens — terracotta
 * list markers, mono inline code, paper-toned tables, sage-free chrome.
 *
 * Used by both:
 * - CachedAnalysisView (per-section card bodies, post-split)
 * - RequestAdvisorBody (live SSE stream, whole reasoning blob)
 *
 * react-markdown sanitises by default — no `dangerouslySetInnerHTML` and no
 * raw HTML pass-through, so model output is safe to render verbatim.
 */
const MD_COMPONENTS: Components = {
  p: ({ children }) => <p style={PARAGRAPH_STYLE}>{children}</p>,
  ul: ({ children }) => <ul style={UL_STYLE}>{children}</ul>,
  ol: ({ children }) => <ol style={OL_STYLE}>{children}</ol>,
  li: ({ children }) => <li style={LI_STYLE}>{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={LINK_STYLE}
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isInline = !className
    if (isInline) {
      return <code style={INLINE_CODE_STYLE}>{children}</code>
    }
    return <code className={className}>{children}</code>
  },
  pre: ({ children }) => <pre style={PRE_STYLE}>{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote style={BLOCKQUOTE_STYLE}>{children}</blockquote>
  ),
  h1: ({ children }) => (
    <h1 style={{ ...HEADING_BASE, fontSize: 15 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ ...HEADING_BASE, fontSize: 14 }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ ...HEADING_BASE, fontSize: 13 }}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 style={{ ...HEADING_BASE, fontSize: 13 }}>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 style={{ ...HEADING_BASE, fontSize: 12 }}>{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 style={{ ...HEADING_BASE, fontSize: 12 }}>{children}</h6>
  ),
  table: ({ children }) => (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE}>{children}</table>
    </div>
  ),
  th: ({ children }) => <th style={TH_STYLE}>{children}</th>,
  td: ({ children }) => <td style={TD_STYLE}>{children}</td>,
  hr: () => <hr style={HR_STYLE} />,
}

export function AnalysisMarkdown({ text }: AnalysisMarkdownProps): JSX.Element {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={MD_COMPONENTS}
    >
      {text}
    </ReactMarkdown>
  )
}

// Convenience re-export for callers that want to render text as a single React
// node (matches the old `renderSectionBody` / `Markdown` signatures).
export function renderAnalysisMarkdown(text: string): ReactNode {
  return <AnalysisMarkdown text={text} />
}
